import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../auth";
import {
  OBSERVATION_KINDS,
  searchObservations,
  searchPlantNotes,
  type ObservationKind,
} from "../db/search";
import { listPlants } from "../db/plants";
import { listLocations } from "../db/locations";
import { useCurrentGardenId } from "../lib/useCurrentGarden";
import type { PlantRow, LocationRow } from "@garden/types/db";
import type {
  SearchObservationsRow,
  SearchPlantNotesRow,
} from "@garden/types/supabase";
import "./Observations.css";

type Scope = "observations" | "notes" | "both";

const SCOPES: { value: Scope; label: string }[] = [
  { value: "observations", label: "Observations" },
  { value: "notes", label: "Plant notes" },
  { value: "both", label: "Both" },
];

const KIND_LABELS: Record<ObservationKind, string> = {
  general: "General",
  yield: "Yield",
  health: "Health",
  flowering: "Flowering",
  pest: "Pest",
  weather: "Weather",
  move: "Move",
  rotation_note: "Rotation",
};

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export function Observations() {
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState<Scope>("observations");
  const [kindFilter, setKindFilter] = useState<ObservationKind | "all">("all");

  const [obsResults, setObsResults] = useState<SearchObservationsRow[]>([]);
  const [noteResults, setNoteResults] = useState<SearchPlantNotesRow[]>([]);
  const [plants, setPlants] = useState<PlantRow[]>([]);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gardenId = useCurrentGardenId();

  // Load plants + locations once for joining the search results to titles.
  useEffect(() => {
    if (!supabase || !gardenId) return;
    void (async () => {
      try {
        const [p, l] = await Promise.all([
          listPlants(supabase!, gardenId),
          listLocations(supabase!, gardenId),
        ]);
        setPlants(p);
        setLocations(l);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [gardenId]);

  // Debounced search. The RPC is cheap (GIN-indexed websearch_to_tsquery),
  // so 200 ms is plenty.
  useEffect(() => {
    if (!supabase) return;
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const promises: Promise<unknown>[] = [];
        if (scope !== "notes") {
          promises.push(
            searchObservations(supabase!, query, {
              kind: kindFilter === "all" ? null : kindFilter,
            }).then(setObsResults),
          );
        } else {
          setObsResults([]);
        }
        if (scope !== "observations") {
          promises.push(
            searchPlantNotes(supabase!, query).then(setNoteResults),
          );
        } else {
          setNoteResults([]);
        }
        await Promise.all(promises);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query, scope, kindFilter]);

  const plantById = useMemo(() => {
    const m = new Map<string, PlantRow>();
    for (const p of plants) m.set(p.id, p);
    return m;
  }, [plants]);

  // Filter search results down to the current garden. The search RPCs
  // (search_observations / search_plant_notes) honour RLS, which lets
  // through every garden the user is a member of. For multi-garden
  // users we want to keep results consistent with the rest of the
  // session — only the current garden.
  const scopedObsResults = useMemo(
    () => obsResults.filter((r) => plantById.has(r.plant_id)),
    [obsResults, plantById],
  );
  const scopedNoteResults = useMemo(
    () => noteResults.filter((r) => plantById.has(r.id)),
    [noteResults, plantById],
  );

  const locationById = useMemo(() => {
    const m = new Map<string, LocationRow>();
    for (const l of locations) m.set(l.id, l);
    return m;
  }, [locations]);

  const totalCount = scopedObsResults.length + scopedNoteResults.length;
  const distinctPlantCount = useMemo(() => {
    const ids = new Set<string>();
    for (const r of scopedObsResults) ids.add(r.plant_id);
    for (const r of scopedNoteResults) ids.add(r.id);
    return ids.size;
  }, [scopedObsResults, scopedNoteResults]);

  return (
    <div className="Observations">
      <div className="Observations__head">
        <h1>Observations</h1>
        <div className="Observations__headMeta">
          {totalCount === 0 && !query.trim()
            ? "Search across your journal & notes."
            : `${totalCount} result${totalCount === 1 ? "" : "s"} · ${distinctPlantCount} plant${distinctPlantCount === 1 ? "" : "s"}`}
        </div>
      </div>

      <div className="Observations__search">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <circle cx="7" cy="7" r="4.5" />
          <path d="M10.5 10.5L14 14" />
        </svg>
        <input
          type="text"
          className="Observations__searchInput"
          placeholder="Try 'blackspot', 'prune', 'tomato'…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        {query && (
          <button
            type="button"
            className="Observations__searchClear"
            onClick={() => setQuery("")}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      <div className="Observations__scopeRow">
        <div className="Observations__scope" role="tablist">
          {SCOPES.map((s) => (
            <button
              key={s.value}
              type="button"
              role="tab"
              aria-selected={scope === s.value}
              className={`Observations__scopeBtn${scope === s.value ? " is-active" : ""}`}
              onClick={() => setScope(s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="Observations__scopeMeta">
          {loading ? "Searching…" : scopeLabel(scope, query)}
        </div>
      </div>

      {scope !== "notes" && (
        <div className="Observations__filters">
          <button
            type="button"
            className={`Observations__filter${kindFilter === "all" ? " is-active" : ""}`}
            onClick={() => setKindFilter("all")}
          >
            All kinds
          </button>
          {OBSERVATION_KINDS.map((k) => (
            <button
              key={k}
              type="button"
              className={`Observations__filter${kindFilter === k ? " is-active" : ""}`}
              onClick={() => setKindFilter(k)}
            >
              {KIND_LABELS[k]}
            </button>
          ))}
        </div>
      )}

      {error && <div className="Observations__error">{error}</div>}

      <div className="Observations__list">
        {scope !== "notes" &&
          scopedObsResults.map((r) => (
            <ObservationCard
              key={`obs-${r.id}`}
              row={r}
              plant={plantById.get(r.plant_id)}
              location={
                plantById.get(r.plant_id)?.location_id
                  ? locationById.get(plantById.get(r.plant_id)!.location_id!)
                  : undefined
              }
            />
          ))}
        {scope !== "observations" &&
          scopedNoteResults.map((r) => (
            <NoteCard
              key={`note-${r.id}`}
              row={r}
              location={r.location_id ? locationById.get(r.location_id) : undefined}
            />
          ))}
        {!loading && totalCount === 0 && query.trim() && (
          <div className="Observations__empty">
            No matches for <em>"{query}"</em>{" "}
            {scope === "observations"
              ? "in observations."
              : scope === "notes"
                ? "in plant notes."
                : "anywhere."}
          </div>
        )}
        {!loading && totalCount === 0 && !query.trim() && (
          <div className="Observations__empty">
            Type a word to search. The most recent {scope === "notes" ? "plants with notes" : "observations"} will also appear here.
          </div>
        )}
      </div>
    </div>
  );
}

function scopeLabel(scope: Scope, query: string): string {
  const q = query.trim();
  if (!q) {
    if (scope === "observations") return "Showing recent observations";
    if (scope === "notes") return "Showing plants with notes";
    return "Showing recent entries";
  }
  if (scope === "observations") return "Searching observations only";
  if (scope === "notes") return "Searching plant notes only";
  return "Searching observations and notes";
}

function formatDate(iso: string): { day: string; year: string } {
  const [y, m, d] = iso.split("-");
  const month = MONTHS_SHORT[Number(m) - 1] ?? "?";
  return { day: `${Number(d)} ${month}`, year: y };
}

interface ObservationCardProps {
  row: SearchObservationsRow;
  plant: PlantRow | undefined;
  location: LocationRow | undefined;
}

function ObservationCard({ row, plant, location }: ObservationCardProps) {
  const { day, year } = formatDate(row.observation_date);
  const plantTitle = plant
    ? plantTitleFor(plant)
    : "(plant removed)";
  return (
    <Link
      to={plant ? `/plants/${plant.id}?tab=observations#obs-${row.id}` : "#"}
      className="Observations__card"
    >
      <div className="Observations__cardWhen">
        <div className="Observations__cardDate">{day}</div>
        <div className="Observations__cardYear">{year}</div>
        {row.kind && (
          <span className="Observations__cardKind">
            {KIND_LABELS[(row.kind as ObservationKind)] ?? row.kind}
          </span>
        )}
      </div>
      <div className="Observations__cardContent">
        <div className="Observations__cardPlant">{plantTitle}</div>
        {location && (
          <div className="Observations__cardLoc">
            {location.name}
          </div>
        )}
        {/* body_html comes from ts_headline server-side, with <mark>…</mark>. */}
        <div
          className="Observations__cardBody"
          dangerouslySetInnerHTML={{ __html: row.body_html || "(no body)" }}
        />
      </div>
    </Link>
  );
}

interface NoteCardProps {
  row: SearchPlantNotesRow;
  location: LocationRow | undefined;
}

function NoteCard({ row, location }: NoteCardProps) {
  const updated = new Date(row.updated_at);
  const day = `${updated.getDate()} ${MONTHS_SHORT[updated.getMonth()]}`;
  const year = String(updated.getFullYear());
  return (
    <Link to={`/plants/${row.id}?tab=notes`} className="Observations__card Observations__card--notes">
      <div className="Observations__cardWhen">
        <div className="Observations__cardDate">{day}</div>
        <div className="Observations__cardYear">{year}</div>
        <span className="Observations__cardKind Observations__cardKind--notes">Notes</span>
      </div>
      <div className="Observations__cardContent">
        <div className="Observations__cardPlant">{plantTitleForNote(row)}</div>
        {location && (
          <div className="Observations__cardLoc">{location.name}</div>
        )}
        <div
          className="Observations__cardBody"
          dangerouslySetInnerHTML={{ __html: row.notes_html || "(empty)" }}
        />
      </div>
    </Link>
  );
}

function plantTitleFor(plant: PlantRow): string {
  const bits: string[] = [plant.common_name];
  if (plant.cultivar) bits.push(`'${plant.cultivar}'`);
  return bits.join(" ");
}

function plantTitleForNote(row: SearchPlantNotesRow): string {
  const bits: string[] = [row.common_name];
  if (row.cultivar) bits.push(`'${row.cultivar}'`);
  return bits.join(" ");
}
